import { useEffect } from "react";

import { getSupabaseClient } from "../providers/supabase/supabase";

// The same message is sent once per tab, so a render loop is one line, not 1000
const sent = new Set<string>();

/** Sends a browser error to the system log (developer's log page) */
export const logClientError = (message: string, details: object = {}) => {
  if (import.meta.env.MODE === "test" || sent.has(message)) return;
  sent.add(message);
  getSupabaseClient()
    .rpc("log_client_error", {
      message,
      details: {
        ...details,
        url: window.location.hash || window.location.pathname,
        ua: navigator.userAgent,
        app: import.meta.env.VITE_APP_VERSION ?? null,
      },
    })
    .then(
      () => undefined,
      () => undefined,
    );
};

/** Catches uncaught errors and rejected promises of the signed-in session */
export const useErrorLog = () => {
  useEffect(() => {
    const onError = (event: ErrorEvent) =>
      logClientError(event.message || "Error", {
        stack: event.error?.stack?.slice(0, 1500),
        at: `${event.filename}:${event.lineno}:${event.colno}`,
      });
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      logClientError(
        reason instanceof Error ? reason.message : String(reason).slice(0, 300),
        {
          stack: reason instanceof Error ? reason.stack?.slice(0, 1500) : null,
        },
      );
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
};
